/**
 * Chat Dialogs Container
 * 
 * Manages all dialog states for the chat: regenerate confirmation, bookmarks, branches, and merges.
 * Memoized for performance optimization.
 */

import { memo } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { BookmarkPanel } from "./BookmarkPanel";
import { BranchDialog } from "./BranchDialog";
import { MergeDialog } from "./MergeDialog";

import type { Session, Message } from "@/types/state";

interface ChatDialogsContainerProps {
  sessionId: string;
  
  // Regenerate dialog
  showRegenerateDialog: boolean;
  onRegenerateConfirm: () => void;
  onRegenerateCancel: () => void;
  
  // Bookmark panel
  showBookmarkPanel: boolean;
  onBookmarkPanelChange: (open: boolean) => void;
  onJumpToMessage: (messageId: string) => void;
  
  // Branch dialog
  branchDialogOpen: boolean;
  branchingFromMessage: Message | null;
  onBranchDialogChange: (open: boolean) => void;
  onCreateBranch: (branchName: string) => void;
  
  // Merge dialog
  mergeDialogOpen: boolean;
  selectedMergeBranch: Session | null;
  currentSession: Session | null;
  onMergeDialogChange: (open: boolean) => void;
  onInitiateMerge: (
    strategy: "merge_into_existing" | "create_new_branch",
    resolutions: Record<number, "source" | "target" | "both" | "neither">,
    newSessionName?: string
  ) => void;
  onAnalyzeMerge: () => Promise<any>;
}

const ChatDialogsContainerComponent = ({
  sessionId,
  showRegenerateDialog,
  onRegenerateConfirm,
  onRegenerateCancel,
  showBookmarkPanel,
  onBookmarkPanelChange,
  onJumpToMessage,
  branchDialogOpen,
  branchingFromMessage,
  onBranchDialogChange,
  onCreateBranch,
  mergeDialogOpen,
  selectedMergeBranch,
  currentSession,
  onMergeDialogChange,
  onInitiateMerge,
  onAnalyzeMerge,
}: ChatDialogsContainerProps) => {
  return (
    <>
      {/* Regenerate Confirmation Dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={onRegenerateCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate Response?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the current response and all messages after it, then generate a new response. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRegenerateConfirm}>
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bookmark Panel */}
      <BookmarkPanel
        sessionId={sessionId}
        open={showBookmarkPanel}
        onOpenChange={onBookmarkPanelChange}
        onJumpToMessage={onJumpToMessage}
      />

      {/* Branch Dialog */}
      <BranchDialog
        open={branchDialogOpen}
        onOpenChange={onBranchDialogChange}
        onBranch={onCreateBranch}
        messageContent={branchingFromMessage?.content || ""}
      />

      {/* Merge Dialog */}
      {selectedMergeBranch && currentSession && (
        <MergeDialog
          open={mergeDialogOpen}
          onOpenChange={onMergeDialogChange}
          sourceBranchName={selectedMergeBranch.branch_name || selectedMergeBranch.name}
          targetBranchName={currentSession.branch_name || currentSession.name}
          onInitiateMerge={onInitiateMerge as any}
          onAnalyze={onAnalyzeMerge}
        />
      )}
    </>
  );
};

export const ChatDialogsContainer = memo(ChatDialogsContainerComponent);
